// Put shared types here

import type {
  ConfirmationGroup,
  IFileState,
  IMultiSelectItem,
  IPagerResult,
  IValidationResult,
} from "@/interfaces";
import type {
  App_Module,
  Approval_State,
  PlannerNotificationSettingsInput,
  Plannertask_Assignee_Type,
  Profile_Type,
  Scalars,
} from "@/graphql/generated-types";
import { type Component, type ComputedRef } from "vue";
import type { Delta } from "quill";
import type { ICalendarItem } from "@/stories/dashboard/CalendarItem/CalendarItem.vue";
import type { PARTICIPANT_KEYS } from "@/consts";
import type ChangeTracker from "@/helpers/forms/ChangeTracker";
import type { RRuleFeatureState } from "@/helpers/features/RRuleFeature";
import type { OccurrencesResult } from "@/api/repeat";
import type { PlannerTaskFragment } from "@/graphql";
import type { SVGName } from "@/SVGs";

export type Environments = "development" | "staging" | "production";

export type SizeTypes = "sm" | "md" | "lg";

export type ExtendedSizeTypes = "xxs" | "xs" | "sm" | "md" | "lg" | "xl";

export type PlacementTypes = "right" | "left" | "auto";

export type HourFormatTypes = "24h" | "12h";

export type StoreName =
  | "NEWS"
  | "ADD_NEWS"
  | "ACTIVITIES"
  | "ADD_ACTIVITIES"
  | "MEETINGS"
  | "ADD_MEETINGS"
  | "MEALS"
  | "ADD_MEALS"
  | "MEALS_DISHES"
  | "MEALS_ADD_DISHES"
  | "EMPLOYEES"
  | "EMPLOYEES_TITLES"
  | "EMPLOYEES_ADD_TITLES"
  | "ADD_EMPLOYEES"
  | "CORE_NEWS"
  | "ADD_CORE_NEWS"
  | "AUTHORS"
  | "ADD_AUTHORS"
  | "PLANNER_TEMPLATE_FORM"
  // categories
  | "ACTIVITIES_CATEGORIES"
  | "ACTIVITIES_ADD_CATEGORIES"
  | "NEWS_CATEGORIES"
  | "NEWS_ADD_CATEGORIES"
  | "MEETINGS_CATEGORIES"
  | "MEETINGS_ADD_CATEGORIES"
  | "MEETINGS_AGENDA_POINTS"
  | "MEETINGS_ADD_AGENDA_POINTS";

export type TabErrorSchemaTypes = Record<string, string[]>;

export type StyleVariants = "primary" | "secondary";

export type ButtonTypes =
  | "primary"
  | "white"
  | "green"
  | "error"
  | "grey"
  | "black"
  | "transparent";

export type AlignmentTypes = "left" | "right" | "center";

export type ManagerTypes = "IBG_MANAGER" | "BP_MANAGER";

export type FileCategory = "image" | "pdf" | "document";

export type days =
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday"
  | "sunday";

export type rruleCadenceOptions = 1 | 7 | 30 | 365;

export type RruleDaysState = Record<number, boolean>;

export type RRuleState = {
  startDate?: number;
  cadence: number;
  occurence: number;
  days: RruleDaysState;
  repeatAmount?: number;
  dayOrder?: number;
  daySelection?: number;
  endingType?: RepeatRuleEndingTypes;
  endingDate?: number;
  occurrences?: OccurrencesResult;
  useBysetpos?: boolean;
};

export type RepeatRuleEndingTypes =
  | "finish_on"
  | "finish_after"
  | "no_end_date";

/**
 * How the list item is selected? By a user click, or selected by a code?
 *
 * It's important to show the selected item modal on list (news, activities, etc).
 * We should show the selected data on the right, always. But we shouldn't show
 * the modal on mobile directly if the selection doesn't come from a user action.
 */
export type ItemSelectionType = "byUser" | "byCode";

export type NullableId = string | null | undefined;

export type Section<ValueType = string> = {
  title: string;
  value: ValueType;
  component: Component;
  isRequired?: boolean;
  managerType?: ManagerTypes;
  onlyOnCreate?: boolean;
  onlyOnExisting?: boolean;
};

export type DropdownDrawerTypes = "auto" | "mobile" | "desktop";

export type DisplayStrategy = "auto" | "show" | "hide";

export type SupportedLocales = "da" | "en" | "de" | "no";

export type NullableBoolean = null | undefined | boolean;

export type Modules =
  | "news"
  | "activities"
  | "employees"
  | "meetings"
  | "workSchedule"
  | "booking"
  | "meals"
  | "media"
  | "groups"
  | "planner"
  | "admin";

export type EditRepeatTypes = "single" | "repeating";

export type PrimitiveModel = number | string | null | undefined;

export type ListFilterTypes = "categories" | "groups" | "departments";

export type CategoryViews = "activities" | "news" | "meetings";

export type LoadingType = "none" | "loading" | "refetching" | "loaded";

export type WidthStrategy = "fit" | "full";

export type PageableQuery<TData, TVariables> = (
  variables: TVariables,
) => Promise<IPagerResult<TData>>;

export type PagerStatus =
  | "notStartedYet"
  | "firstChunk"
  | "otherChunks"
  | "done";

export type BaseFilters = {
  id?: NullableId;
  searchText?: string;
  section: string;
};

export type VueVirtualScrollRef = {
  scrollToItem(index: number): void;
};

export type VirtualScrollTo = (index: number) => void;

export type IdType = string | number | undefined | null;

export type RRuleSupportedEntityType =
  | "RepeatingActivity"
  | "SingleActivity"
  | "RepeatingNews"
  | "SingleNews"
  | "SingleMeeting"
  | "RepeatingMeeting"
  | "SingleMeal"
  | "RepeatingMeal";

export type RRuleSupportedStore = {
  item: RRuleFeatureState;
};

export type PatchableStore<T> = T & {
  $patch: (data: Partial<T>) => void;
};
export type EditorModel = Delta | string;

export type ListViewScope =
  | "show_upcoming"
  | "show_previous"
  | "show_external_content";

export type ActivityViewFilters = BaseFilters & {
  filter?: ListViewScope;
  showNewModal: ActiveStatus;
  showOnlyVisible: ActiveStatus;
};

export type NewsViewFilters = BaseFilters & {
  filter?: ListViewScope;
  showNewModal: ActiveStatus;
  showOnlyVisible: ActiveStatus;
};

export type MeetingViewFilters = BaseFilters & {
  filter?: ListViewScope;
  showNewModal: ActiveStatus;
};

export type MealViewFilters = BaseFilters & {
  filter?: ListViewScope;
  showNewModal: ActiveStatus;
};

export type EmptyStateStatus = "none" | "page_empty" | "no_matched_items";

export type ContentStatus = "white" | "visible" | "warning";

export type EntityWithId = {
  id: Scalars["ID"]["output"];
};

export type LegacyUserRole = {
  role: {
    name: string;
  };
};

export type ActiveStatus = "on" | "off";

export type DatePresets =
  | "time"
  | "date"
  | "date-time"
  | "long-date"
  | "long-date-time";

export type SlideDirections = "animate-slide-left" | "animate-slide-right";

export type MediaType = "video" | "image" | "file";

export type PlannerFilterItem = {
  id: string;
  title: string;
  userTypes: Plannertask_Assignee_Type[];
  departments: IMultiSelectItem[];
  categories: IMultiSelectItem[];
  people: IMultiSelectItem[];
  user?: {
    id?: string;
  };
};

export type WCAGValueTypes = "cpr" | "string" | "telefon" | "number";

export type NewFilterTypes = {
  id: string;
  title: string;
  assigneeTypes: Plannertask_Assignee_Type[];
  categories: string[];
  departments: string[];
  employees: string[];
  residents: string[];
  user: { id: string } | null;
};

export type CalendarModules = "meal" | "activity" | "booking" | "meeting";

export type DateRange = {
  dateFrom: string;
  dateTo: string;
};

export type DateRangeAsObject = {
  start: Date;
  end: Date;
};

export type CalendarLinkGenerator = (item: ICalendarItem) => string;

export type ConfirmationDetail =
  | {
      onConfirm?: () => void;
      onCancel?: () => void;
      variant: ButtonTypes;
      textType: "default" | "unsavedChanges";
      custom?: never;
    }
  | {
      onConfirm?: () => void;
      onCancel?: () => void;
      variant: ButtonTypes;
      textType?: never;
      custom: ConfirmationGroup;
    };

export type PermissionSubjects =
  | "planner"
  | "dashboard"
  | "meeting"
  | "news"
  | "activities";

export type PermissionActions =
  | "view"
  | "create"
  | "update"
  | "deletable"
  | "viewOld";

export type ListResourceType = "internal" | "external";

export type DefaultSectionProps = {
  storeName: StoreName;
  isEditable?: boolean;
};

export type DefaultFilterType =
  | "show_upcoming"
  | "show_previous"
  | "show_external_content";

export type VisibleStateTypes = "visible" | "hidden";

// Automatically converts the enum’s string values into a literal union type,
// ensuring strict type safety without manually maintaining the union.
export type AppModule = `${App_Module}`;

// Derive types from the arrays (maintains type safety)
export type SignupParticipants = {
  [K in (typeof PARTICIPANT_KEYS.SIGNUP)[number]]: string[];
};

export type ResponsibleParticipants = {
  [K in (typeof PARTICIPANT_KEYS.RESPONSIBLE)[number]]: string[];
};

export type ParticipantData = SignupParticipants & ResponsibleParticipants;

export type ParticipantState = {
  data: ParticipantData;
  tracker: ChangeTracker;
};

export type VideoProvider = "DREAMBROKER" | "VIMEO" | "YOUTUBE";

export type TimeBasedEvent = {
  startDate: string;
  endDate: string;
};

export type FallbackImageType = "meeting" | "corenews";

export type ViewModule =
  | "activity"
  | "meeting"
  | "meal"
  | "news"
  | "employees"
  | "coreNews";

export type ViewModuleBranch =
  | "root"
  | "categories"
  | "mealDishes"
  | "agendaItems"
  | "titles"
  | "authors";

export type TabMap = Partial<Record<ViewModuleBranch, Section[]>>;

export type MeetingSuggestionType = {
  id: string;
  title: string;
  status: boolean;
  profileType: Profile_Type;
  profileName: string;
  profileImageURL?: string;
};

export type SwitchItemFunction = (
  id: IdType,
  type: ItemSelectionType,
) => Promise<void>;

export type VisibilityTypes = "now" | "beforeXMinutes" | "onStartTime";

export type VisibilityOptions = {
  visibilityType: VisibilityTypes;
  visibleFromDays?: number;
  visibleFromHours?: number;
  visibleFromMinutes?: number;
  isVisible?: boolean;
};

export type VisibleEvent = {
  showFrom?: null | number;
  startDate: number;
  endDate: number;
};

export type DefaultState = {
  validation: IValidationResult | null;
  tabErrors: Record<string, boolean>;
  originalHash: string | null;
};

export type DatePeriod = {
  /** ISO date string in YYYY-MM-DD format */
  start?: string;
  /** ISO date string in YYYY-MM-DD format */
  end?: string;
};

export type WeekItem = DatePeriod & {
  number: string;
  isSelected: boolean;
  isCurrentWeek: boolean;
  range?: string; // Examples: "Jan 1-4", "Jan 26-Feb 1."
};

export type DatePeriodTypes = "days" | "weeks";

export type WhenFilterOption =
  | "today"
  | "last7"
  | "last30"
  | "last90"
  | "last180"
  | "custom";

export type WhenFilterState = {
  option: WhenFilterOption;
  customFrom?: string;
  customTo?: string;
};

export type DatePeriodSelection = {
  period: DatePeriod;
  type: DatePeriodTypes;
};

export type MobileSupportedFilterContent = {
  isSaveable: ComputedRef<boolean>;
  resetPreview: () => void;
  applyPreview: () => void;
  reset: () => void;
  apply: () => void;
};

export type PreviewBasedState<T> = {
  state: T;
  preview: T;
};

export type MobileFilterPreviewResponse = {
  type: "text" | "badge";
  value: string | number;
};

export type FilterTypes =
  | "categories"
  | "groups"
  | "departments"
  | "period"
  | "day"
  | "titles"
  | "modules"
  | "residents"
  | "employees"
  | "customFilter"
  | "status"
  | "when";

export type ListFilterOptions = {
  saveButtonDisplayment?: DisplayStrategy;
};

export type ListFilterConfiguration = {
  name: FilterTypes;
  title: string;
  component: Component;
  desktopComponent?: Component;
  contentComponent?: Component;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selection: (data: any) => MobileFilterPreviewResponse | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getDefault: () => any;
  props?: Record<string, unknown>;
  contentProps?: Record<string, unknown>;
  icon?: SVGName;
  options?: ListFilterOptions;
};

export type DefaultMobileFilterProps<T> = {
  modelValue: PreviewBasedState<T>;
};

export type DefaultMobileFilterEmits<T> = {
  "update:modelValue": [PreviewBasedState<T>];
};

export type WeekState = {
  /** ISO date string in YYYY-MM-DD format */
  start: string;
  /** ISO date string in YYYY-MM-DD format */
  end: string;
};

export const PLANNER_MODULE_FILTER_TYPES = [
  "plannerTask",
  "meeting",
  "booking",
  "meal",
  "activity",
] as const;

export type PlannerModuleFilterType =
  (typeof PLANNER_MODULE_FILTER_TYPES)[number];

export type PlannerItem = PlannerTaskFragment & {
  itemSource: "task" | "entity";
  entityType?: PlannerModuleFilterType;
};

export type TimeSlot = {
  slotTime: Date;
  isNow: boolean;
  items: PlannerItem[];
};

/**
 * Image source type for MultiSourceImageInput component
 * Supports file upload, Pixabay library, and pictogram selection
 */
export type ImageSourceType = "upload" | "pixabay" | "pictogram";

export type MultiSourceImageInputModel = {
  imageType: ImageSourceType;
  images: IFileState[];
};

export type PlannerSubtask = {
  key: string;
  id?: string;
  title: string;
  image?: IFileState;
  mode: "edit" | "data";
  validation?: IValidationResult;
};

export type SharedImageListProps = {
  isSelecteableMore: boolean;
  max?: number;
};

export type FormType = "create" | "edit" | "duplicate";

export type PlannerFormItem = {
  type: FormType;
  id?: string;
  validation?: IValidationResult;
  imageType: ImageSourceType;
  images: IFileState[];
  title: string;
  startDate: number;
  endDate: number;
  category?: string;
  description: string;
  rruleState?: RRuleState;
  residents: string[];
  departments: string[];
  employees: string[];
  subtasks: PlannerSubtask[];
  isRepeating?: boolean;
  notificationSettings: PlannerNotificationSettingsInput[];
  visibleForResident: boolean;
  approvalState: Approval_State;
};

export type AssigneeType =
  PlannerTaskFragment["assignees"][number]["__typename"];

export type MultiSelectListDialog = {
  titleKey: string;
  selectedItems: IMultiSelectItem[];
};

export type GlobalSearchResult = {
  id: string;
  title: string;
  description?: string;
  imageURL?: string;
  categoryColor?: string;
};

export type PlannerSearchResult = GlobalSearchResult & {
  entityType?: PlannerModuleFilterType;
  categoryTitle: string;
  categoryId?: string;
  start: number;
  end: number;
  completed: boolean;
  residents: string[];
  employees: string[];
  departments: string[];
  isRepeating: boolean;
};
